import React, { useState } from "react";
import { Box, Button, Flex, FormDialog, TextField, Typography, Select, MenuItem, Checkbox } from "flux";
import { BaseDialogProps } from "@adora/shared/contexts/dialog-context";
import { TRACKING_ID_PLACEHOLDER } from "@adora/shared/utils/constants";
import uploadFile from "../../utils/upload-ad-asset";
import useMetaCTATypes from "@adora/shared/hooks/use-meta-cta-types";
import useGoogleCTATypes from "@adora/shared/hooks/use-google-cta-types";
import useMsanCTATypes from "@adora/shared/hooks/use-msan-cta-types";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import { AdAssetData } from "../../types/ad-asset";
import useMutateAdAssetGroup from "../../hooks/use-mutate-ad-asset-group";
import {
  PublisherPlatforms,
  EnabledPlatforms,
  FacebookPlacementSettings,
  FacebookPlacementPositions,
  InstagramPlacementPositions,
  MessengerPlacementPositions,
  AudienceNetworkPlacementPositions
} from "@adora/shared/features/ad-publishing/types";
import PlatformCheckboxes from "@adora/shared/components/ad-asset-group-common/platform-checkboxes";
import AspectRatioCheckboxes from "@adora/shared/components/ad-asset-group-common/aspect-ratio-checkboxes";
import ConfirmationDialog from "@adora/shared/components/confirm-dialog";
import ErrorDialog from "../error-dialog";
import { ALL_PLATFORMS } from "@adora/shared/types/ad-platforms";
import { useTrackingIdInclusion } from "@adora/shared";
import DestinationUrlField from "@adora/shared/components/ad-asset-group-common/destination-url-field";
import useThemes from "../../hooks/use-themes";

interface CreateAdAssetGroupDialogProps extends BaseDialogProps {
  brandId: number;
}

interface CreateAdAssetGroupData extends Omit<AdAssetData, "name" | "facebook_placement_settings"> {
  brand_id: number;
  name: string;
  description: string;
  platforms: string[];
  tracking_id_start: number;
  google_ad_group_id?: string;
  meta_ad_set_id?: string;
  msan_ad_group_id?: string;
  theme_id?: number;
  ad_name: string;
  facebook_placement_settings: FacebookPlacementSettings;
  google_campaign_name?: string;
  google_ad_group_name?: string;
  google_ad_group_url?: string;
  msan_campaign_name?: string;
  msan_ad_group_name?: string;
  msan_ad_group_url?: string;
  meta_campaign_name?: string;
  meta_ad_set_name?: string;
  meta_ad_set_url?: string;
  aspect_ratios?: string[];
}

const placementSettingsInitialState = {
  platforms: {
    [PublisherPlatforms.FACEBOOK]: false,
    [PublisherPlatforms.INSTAGRAM]: false,
    [PublisherPlatforms.AUDIENCE_NETWORK]: false,
    [PublisherPlatforms.MESSENGER]: false
  },
  facebook_positions: [],
  instagram_positions: [],
  audience_network_positions: [],
  messenger_positions: []
};

const CreateAdAssetGroupDialog: React.FC<CreateAdAssetGroupDialogProps> = ({ open, onClose, brandId }) => {
  const [formData, setFormData] = useState<CreateAdAssetGroupData>({
    brand_id: brandId,
    name: "",
    description: "",
    platforms: [],
    tracking_id_start: 1,
    google_ad_group_id: "",
    meta_ad_set_id: "",
    msan_ad_group_id: "",
    theme_id: undefined,
    google_headlines: ["", "", "", "", ""],
    google_long_headline: "",
    google_descriptions: ["", "", "", "", ""],
    google_call_to_action_type: "",
    google_destination_url: TRACKING_ID_PLACEHOLDER,
    facebook_headlines: ["", "", "", "", ""],
    facebook_bodies: ["", "", "", "", ""],
    facebook_description: "",
    facebook_call_to_action_type: "",
    facebook_placement_settings: placementSettingsInitialState,
    facebook_destination_url: TRACKING_ID_PLACEHOLDER,
    msan_headlines: ["", "", "", "", ""],
    msan_long_headline: "",
    msan_descriptions: ["", "", "", "", ""],
    msan_call_to_action_type: "",
    msan_destination_url: TRACKING_ID_PLACEHOLDER,
    ad_name: TRACKING_ID_PLACEHOLDER,
    google_campaign_name: "",
    google_ad_group_name: "",
    google_ad_group_url: "",
    msan_campaign_name: "",
    msan_ad_group_name: "",
    msan_ad_group_url: "",
    meta_campaign_name: "",
    meta_ad_set_name: "",
    meta_ad_set_url: "",
    aspect_ratios: []
  });
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isPlacementExpanded, setIsPlacementExpanded] = useState(false);

  // Use the shared tracking ID inclusion hook
  const { trackingIdInclusion, handleDestinationUrlChange, handleTrackingIdInclusionChange } = useTrackingIdInclusion({
    onFieldChange: (field: string, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  });

  const { create: createAdAssetGroup, isLoading, error } = useMutateAdAssetGroup();
  const { themes } = useThemes(brandId);
  const { ctaTypes: metaCtaTypes, isLoading: isLoadingMetaCTATypes, error: metaCtaTypesError } = useMetaCTATypes();
  const {
    ctaTypes: googleCtaTypes,
    isLoading: isLoadingGoogleCTATypes,
    error: googleCtaTypesError
  } = useGoogleCTATypes();
  const { ctaTypes: msanCtaTypes, isLoading: isLoadingMsanCTATypes, error: msanCtaTypesError } = useMsanCTATypes();

  const { openDialog } = useDialog();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setUploading(true);

    try {
      let result;
      if (file) {
        const s3Path = await uploadFile(file, brandId);

        result = await createAdAssetGroup({
          ...formData,
          image_folder_path: s3Path
        });
      } else {
        result = await createAdAssetGroup(formData);
      }

      result.match(
        (error) => {
          openDialog(ErrorDialog, {
            title: "Error",
            error: error
          });
        },
        (response) => {
          if (response) onClose(true);
        }
      );
    } catch (error_) {
      console.error("Unknown error", error_);
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (field: keyof CreateAdAssetGroupData, value: string | string[] | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTrackingIdChange = (field: keyof CreateAdAssetGroupData, newValue: string) => {
    const protectedStringIndex = newValue.indexOf(TRACKING_ID_PLACEHOLDER);
    if (protectedStringIndex === -1) {
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: newValue }));
  };

  const handleListInputChange = (field: keyof CreateAdAssetGroupData, index: number, value: string) => {
    setFormData((prev) => {
      const newList = [...(prev[field] as string[])];
      newList[index] = value;
      return { ...prev, [field]: newList };
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const fileName = event.target.files[0].name;
      const extension = fileName.split(".").pop()?.toLowerCase();
      if (extension !== "zip") {
        openDialog(ConfirmationDialog, {
          title: "Error",
          description: "File must be a '.zip' file",
          showCancel: false,
          danger: true,
          primaryButtonText: "Ok"
        });
      } else {
        setFile(event.target.files[0]);
      }
    }
  };

  const handlePlatformChange = (platforms: EnabledPlatforms) => {
    setFormData((prev) => ({
      ...prev,
      facebook_placement_settings: {
        ...prev.facebook_placement_settings,
        platforms
      }
    }));
  };

  const handlePositionsChange = (
    platform: keyof Omit<FacebookPlacementSettings, "platforms">,
    positions:
      | FacebookPlacementPositions[]
      | InstagramPlacementPositions[]
      | MessengerPlacementPositions[]
      | AudienceNetworkPlacementPositions[]
  ) => {
    setFormData((prev) => ({
      ...prev,
      facebook_placement_settings: {
        ...prev.facebook_placement_settings,
        [platform]: positions
      }
    }));
  };

  const renderCampaignFields = () => {
    return (
      <>
        {formData.platforms.includes("google") && (
          <>
            <TextField
              label="Google Campaign Name"
              value={formData.google_campaign_name}
              onChange={(e) => handleInputChange("google_campaign_name", e.target.value)}
              dataDogIdentifier="google campaign name"
              fullWidth
            />
            <TextField
              label="Google Ad Group ID"
              value={formData.google_ad_group_id || ""}
              onChange={(e) => handleInputChange("google_ad_group_id", e.target.value)}
              fullWidth
              placeholder="Optional"
              dataDogIdentifier="google ad group id"
            />
            <TextField
              label="Google Ad Group Name"
              value={formData.google_ad_group_name}
              onChange={(e) => handleInputChange("google_ad_group_name", e.target.value)}
              fullWidth
              dataDogIdentifier="google ad group name"
            />
            <TextField
              label="Google Ad Group URL"
              value={formData.google_ad_group_url}
              onChange={(e) => handleInputChange("google_ad_group_url", e.target.value)}
              fullWidth
              dataDogIdentifier="google ad group url"
            />
            <DestinationUrlField
              label="Google Destination URL"
              value={formData.google_destination_url || ""}
              field="google_destination_url"
              isTrackingIdIncluded={trackingIdInclusion.google_destination_url}
              onValueChange={handleDestinationUrlChange}
              onTrackingIdInclusionChange={handleTrackingIdInclusionChange}
              dataDogIdentifier="google destination url"
            />
            {/* Google Headlines */}
            {formData.google_headlines.map((headline, index) => (
              <TextField
                key={`google-headline-${index}`}
                label={`Google Headline ${index + 1}`}
                value={headline}
                onChange={(e) => handleListInputChange("google_headlines", index, e.target.value)}
                fullWidth
                dataDogIdentifier="google headline"
              />
            ))}
            {/* Google Long Headline */}
            <TextField
              label="Google Long Headline"
              value={formData.google_long_headline}
              onChange={(e) => handleInputChange("google_long_headline", e.target.value)}
              fullWidth
              inputProps={{ maxLength: 90 }}
              dataDogIdentifier="google long headline"
            />
            {/* Google Descriptions */}
            {formData.google_descriptions.map((description, index) => (
              <TextField
                key={`google-description-${index}`}
                label={`Google Description ${index + 1}`}
                value={description}
                onChange={(e) => handleListInputChange("google_descriptions", index, e.target.value)}
                fullWidth
                dataDogIdentifier="google description"
              />
            ))}
            {/* Google Call to Action Types */}
            <Select
              value={formData.google_call_to_action_type}
              onChange={(e) => handleInputChange("google_call_to_action_type", e.target.value as string)}
              displayEmpty
              disabled={isLoadingGoogleCTATypes}
            >
              <MenuItem value="">Google Call To Action</MenuItem>
              {isLoadingGoogleCTATypes ? (
                <MenuItem disabled>Loading CTA types...</MenuItem>
              ) : googleCtaTypesError ? (
                <MenuItem disabled>Error loading CTA types</MenuItem>
              ) : (
                googleCtaTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))
              )}
            </Select>
          </>
        )}

        {formData.platforms.includes("msan") && (
          <>
            <TextField
              label="MSAN Campaign Name"
              value={formData.msan_campaign_name}
              onChange={(e) => handleInputChange("msan_campaign_name", e.target.value)}
              fullWidth
              dataDogIdentifier="msan campaign name"
            />
            <TextField
              label="MSAN Ad Group ID"
              value={formData.msan_ad_group_id || ""}
              onChange={(e) => handleInputChange("msan_ad_group_id", e.target.value)}
              fullWidth
              placeholder="Optional"
              dataDogIdentifier="msan ad group id"
            />
            <TextField
              label="MSAN Ad Group Name"
              value={formData.msan_ad_group_name}
              onChange={(e) => handleInputChange("msan_ad_group_name", e.target.value)}
              fullWidth
              dataDogIdentifier="msan ad group name"
            />
            <TextField
              label="MSAN Ad Group URL"
              value={formData.msan_ad_group_url}
              onChange={(e) => handleInputChange("msan_ad_group_url", e.target.value)}
              fullWidth
              dataDogIdentifier="msan ad group url"
            />
            <DestinationUrlField
              label="MSAN Destination URL"
              value={formData.msan_destination_url || ""}
              field="msan_destination_url"
              isTrackingIdIncluded={trackingIdInclusion.msan_destination_url}
              onValueChange={handleDestinationUrlChange}
              onTrackingIdInclusionChange={handleTrackingIdInclusionChange}
              dataDogIdentifier="msan destination url"
            />
            {/* MSAN Headlines */}
            {formData.msan_headlines.map((headline, index) => (
              <TextField
                key={`msan-headline-${index}`}
                label={`MSAN Headline ${index + 1}`}
                value={headline}
                onChange={(e) => handleListInputChange("msan_headlines", index, e.target.value)}
                fullWidth
                dataDogIdentifier="msan headline"
              />
            ))}
            {/* MSAN Long Headline */}
            <TextField
              label="MSAN Long Headline"
              value={formData.msan_long_headline}
              onChange={(e) => handleInputChange("msan_long_headline", e.target.value)}
              fullWidth
              inputProps={{ maxLength: 90 }}
              dataDogIdentifier="msan long headline"
            />
            {/* MSAN Descriptions */}
            {formData.msan_descriptions.map((description, index) => (
              <TextField
                key={`msan-description-${index}`}
                label={`MSAN Description ${index + 1}`}
                value={description}
                onChange={(e) => handleListInputChange("msan_descriptions", index, e.target.value)}
                fullWidth
                dataDogIdentifier="msan description"
              />
            ))}
            {/* MSAN Call to Action Types */}
            <Select
              value={formData.msan_call_to_action_type}
              onChange={(e) => handleInputChange("msan_call_to_action_type", e.target.value as string)}
              displayEmpty
              disabled={isLoadingMsanCTATypes}
            >
              <MenuItem value="">MSAN Call To Action</MenuItem>
              {isLoadingMsanCTATypes ? (
                <MenuItem disabled>Loading CTA types...</MenuItem>
              ) : msanCtaTypesError ? (
                <MenuItem disabled>Error loading CTA types</MenuItem>
              ) : (
                msanCtaTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))
              )}
            </Select>
          </>
        )}

        {formData.platforms.includes("meta") && (
          <>
            <TextField
              label="Meta Campaign Name"
              value={formData.meta_campaign_name}
              onChange={(e) => handleInputChange("meta_campaign_name", e.target.value)}
              fullWidth
              dataDogIdentifier="meta campaign name"
            />
            <TextField
              label="Meta Ad Set ID"
              value={formData.meta_ad_set_id || ""}
              onChange={(e) => handleInputChange("meta_ad_set_id", e.target.value)}
              fullWidth
              placeholder="Optional"
              dataDogIdentifier="meta ad set id"
            />
            <TextField
              label="Meta Ad Set Name"
              value={formData.meta_ad_set_name}
              onChange={(e) => handleInputChange("meta_ad_set_name", e.target.value)}
              fullWidth
              dataDogIdentifier="meta ad set name"
            />
            <TextField
              label="Meta Ad Set URL"
              value={formData.meta_ad_set_url}
              onChange={(e) => handleInputChange("meta_ad_set_url", e.target.value)}
              fullWidth
              dataDogIdentifier="meta ad set url"
            />

            <DestinationUrlField
              label="Meta Destination URL"
              value={formData.facebook_destination_url || ""}
              field="facebook_destination_url"
              isTrackingIdIncluded={trackingIdInclusion.facebook_destination_url}
              onValueChange={handleDestinationUrlChange}
              onTrackingIdInclusionChange={handleTrackingIdInclusionChange}
              dataDogIdentifier="meta destination url"
            />
            {/* Facebook Bodies */}
            {formData.facebook_bodies.map((body, index) => (
              <TextField
                key={`facebook-body-${index}`}
                label={`Meta Primary Text ${index + 1}`}
                value={body}
                onChange={(e) => handleListInputChange("facebook_bodies", index, e.target.value)}
                fullWidth
                dataDogIdentifier="meta primary text"
              />
            ))}
            {/* Facebook Headlines */}
            {formData.facebook_headlines.map((headline, index) => (
              <TextField
                key={`facebook-headline-${index}`}
                label={`Meta Headline ${index + 1}`}
                value={headline}
                onChange={(e) => handleListInputChange("facebook_headlines", index, e.target.value)}
                fullWidth
                dataDogIdentifier="meta headline"
              />
            ))}
            {/* Facebook Description */}
            <TextField
              label="Meta Description"
              value={formData.facebook_description}
              onChange={(e) => handleInputChange("facebook_description", e.target.value)}
              fullWidth
              dataDogIdentifier="meta description"
            />
            {/* Facebook Call to Action Types */}
            <Select
              value={formData.facebook_call_to_action_type}
              onChange={(e) => handleInputChange("facebook_call_to_action_type", e.target.value as string)}
              displayEmpty
              disabled={isLoadingMetaCTATypes}
            >
              <MenuItem value="">Meta Call To Action</MenuItem>
              {isLoadingMetaCTATypes ? (
                <MenuItem disabled>Loading CTA types...</MenuItem>
              ) : metaCtaTypesError ? (
                <MenuItem disabled>Error loading CTA types</MenuItem>
              ) : (
                metaCtaTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))
              )}
            </Select>
            <Box mt={2}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                onClick={() => setIsPlacementExpanded(!isPlacementExpanded)}
                style={{ cursor: "pointer" }}
              >
                {isPlacementExpanded ? "-" : "+"}
                <Typography variant="subtitle1">Meta Placement Settings</Typography>
              </Box>

              {isPlacementExpanded && (
                <Box mt={2}>
                  <PlatformCheckboxes
                    platforms={formData.facebook_placement_settings.platforms}
                    onChange={handlePlatformChange}
                    positionsData={formData.facebook_placement_settings}
                    onPositionsChange={handlePositionsChange}
                  />
                </Box>
              )}
            </Box>
          </>
        )}
      </>
    );
  };

  const isPlatformsEmpty = formData.platforms.every((p) => p === "");

  return (
    <FormDialog
      open={open}
      onClose={(e) => {
        if (window.confirm("Are you sure you want to close this dialog?")) {
          onClose(e);
        }
      }}
      title="Create Ad Asset Group"
      primaryButtonText="Create"
      onSubmit={handleSubmit}
      isPrimaryButtonDisabled={isLoading || uploading || isPlatformsEmpty}
      dataDogIdentifier="create ad asset group"
    >
      <Flex flexDirection="column" gap={2}>
        <Box mb={4}>
          <input type="file" onChange={handleFileChange} style={{ display: "none" }} id="file-upload-input" />
          <label htmlFor="file-upload-input">
            <Button variant="contained" component="span" dataDogIdentifier="create ad asset group upload zip file">
              Upload Zip File
            </Button>
          </label>
          {file && (
            <Typography variant="body2" mt={1}>
              Selected file: {file.name}
            </Typography>
          )}
        </Box>

        <TextField
          label="Group Name"
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          fullWidth
          dataDogIdentifier="group name"
        />

        <TextField
          label="Description"
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          fullWidth
          dataDogIdentifier="group description"
        />

        <Box>
          <Typography variant="subtitle1">Campaign</Typography>
          <Select
            value={(formData.theme_id ?? "").toString()}
            onChange={(e) => handleInputChange("theme_id", Number(e.target.value))}
            displayEmpty
            fullWidth
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {themes?.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name || `Theme ${t.id}`}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <TextField
          label="Ad Name"
          value={formData.ad_name}
          onChange={(e) => handleTrackingIdChange("ad_name", e.target.value)}
          fullWidth
          dataDogIdentifier="ad name"
        />

        <TextField
          label="Tracking ID Start"
          value={formData.tracking_id_start}
          onChange={(e) => {
            handleInputChange("tracking_id_start", Number.parseInt(e.target.value) || "");
          }}
          fullWidth
          dataDogIdentifier="tracking id start"
        />

        <Box>
          <Typography variant="subtitle1">
            Platforms <span style={{ color: "red" }}>*</span>
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            {ALL_PLATFORMS.map((platform) => (
              <Box key={platform} sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  checked={formData.platforms.includes(platform)}
                  onChange={(e) => {
                    const updatedPlatforms = e.target.checked
                      ? [...formData.platforms, platform]
                      : formData.platforms.filter((p) => p !== platform);

                    handleInputChange("platforms", updatedPlatforms);

                    // Only reset fields for platforms that were removed
                    if (!e.target.checked) {
                      setFormData((prev) => ({
                        ...prev,
                        ...(platform === "google" && {
                          google_campaign_name: "",
                          google_ad_group_name: "",
                          google_ad_group_url: "",
                          google_destination_url: ""
                        }),
                        ...(platform === "meta" && {
                          meta_campaign_name: "",
                          meta_ad_set_name: "",
                          meta_ad_set_url: "",
                          facebook_destination_url: "",
                          facebook_placement_settings: placementSettingsInitialState
                        }),
                        ...(platform === "msan" && {
                          msan_campaign_name: "",
                          msan_ad_group_name: "",
                          msan_ad_group_url: "",
                          msan_destination_url: ""
                        })
                      }));
                    }
                  }}
                  dataDogIdentifier="create ad asset group platform checkbox"
                />
                <Typography>{platform.toUpperCase()}</Typography>
              </Box>
            ))}
          </Box>
          <AspectRatioCheckboxes
            value={formData.aspect_ratios}
            onChange={(aspectRatios) => handleInputChange("aspect_ratios", aspectRatios)}
            dataDogIdentifier="update ad asset group aspect ratio"
          />
          {isPlatformsEmpty && (
            <Typography color="error" variant="caption" mt={1}>
              At least one platform must be selected
            </Typography>
          )}
        </Box>

        {renderCampaignFields()}

        {/* Display error message if there is one */}
        {error && <Typography color="error">{error}</Typography>}
      </Flex>
    </FormDialog>
  );
};

export default CreateAdAssetGroupDialog;
